/**
 * Firebase provider — the live backend.
 *
 * Implements every contract in ../contracts against Firebase Auth, Firestore
 * and Cloud Storage.
 *
 * This folder is the only place in ZIvapp allowed to import from 'firebase/*'.
 * Everything Firestore hands back is run through ../../models before it leaves
 * this file, so no Timestamp object ever reaches a component.
 */

import {
  RecaptchaVerifier, signInWithPhoneNumber, signOut as fbSignOut,
  onAuthStateChanged, deleteUser
} from 'firebase/auth'
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, addDoc,
  query, where, orderBy, limit as fsLimit, startAfter, onSnapshot,
  serverTimestamp, writeBatch, increment, arrayUnion, arrayRemove, Timestamp
} from 'firebase/firestore'
import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject
} from 'firebase/storage'

import { firebaseAuth, db, storage, COLLECTIONS } from './app.js'
import {
  toUser, toChat, toMessage, toStatus, toCall, DEFAULT_ICE_CONFIG
} from '../../models/index.js'
import { directChatId } from '../../utils/id.js'
import { compressImage, imageDimensions, kindOf } from '../../utils/file.js'
import { ZivError, ErrorCode } from '../errors.js'
import { REQUIRED_METHODS } from '../contracts/index.js'
import { getConfig } from '../../config.js'

const STATUS_TTL_MS = 24 * 60 * 60 * 1000

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function uid() {
  const user = firebaseAuth?.currentUser
  if (!user) throw new ZivError(ErrorCode.UNAUTHENTICATED, 'You are signed out.')
  return user.uid
}

function uidOrNull() {
  return firebaseAuth?.currentUser?.uid ?? null
}

/** Translate a FirebaseError into a provider-neutral ZivError. */
function translate(error) {
  const code = error?.code ?? ''
  if (code.includes('permission-denied')) return new ZivError(ErrorCode.PERMISSION, 'You do not have access to this.', { cause: error })
  if (code.includes('unauthenticated'))   return new ZivError(ErrorCode.UNAUTHENTICATED, 'You are signed out.', { cause: error })
  if (code.includes('not-found'))         return new ZivError(ErrorCode.NOT_FOUND, 'This is no longer available.', { cause: error })
  if (code.includes('unavailable') || code.includes('network')) return new ZivError(ErrorCode.NETWORK, 'No connection.', { cause: error })
  if (code.includes('resource-exhausted')) return new ZivError(ErrorCode.QUOTA, 'Service temporarily unavailable.', { cause: error })
  if (code.includes('too-many-requests'))  return new ZivError(ErrorCode.RATE_LIMITED, 'Too many attempts.', { cause: error })
  if (code.includes('invalid-verification-code')) return new ZivError(ErrorCode.CODE_INVALID, 'That code is not correct.', { cause: error })
  if (code.includes('code-expired'))      return new ZivError(ErrorCode.CODE_EXPIRED, 'That code has expired.', { cause: error })
  if (code.includes('invalid-phone-number')) return new ZivError(ErrorCode.INVALID_INPUT, 'That phone number is not valid.', { cause: error })
  return new ZivError(ErrorCode.UNKNOWN, error?.message ?? 'Something went wrong.', { cause: error })
}

/** Wrap an async provider call so callers only ever see ZivError. */
async function guard(fn) {
  try {
    return await fn()
  } catch (error) {
    throw error instanceof ZivError ? error : translate(error)
  }
}

/** onSnapshot with error translation, keeping the unsubscribe contract. */
function watch(queryRef, onData, label) {
  return onSnapshot(
    queryRef,
    onData,
    error => console.error(`[ZIvapp] ${label} subscription failed:`, translate(error))
  )
}

const userDoc     = id => doc(db, COLLECTIONS.users, id)
const chatDoc     = id => doc(db, COLLECTIONS.chats, id)
const messagesCol = chatId => collection(db, COLLECTIONS.chats, chatId, COLLECTIONS.messages)
const messageDoc  = (chatId, id) => doc(db, COLLECTIONS.chats, chatId, COLLECTIONS.messages, id)

// ---------------------------------------------------------------------------
// auth
// ---------------------------------------------------------------------------

let recaptcha = null
let pendingConfirmation = null
let cachedUser = null

/**
 * Phone sign-in needs a reCAPTCHA widget. 'invisible' means the user never
 * sees it unless Firebase decides the request looks suspicious.
 */
/**
 * The verifier Firebase requires before it will send an SMS.
 *
 * This used to build an invisible reCAPTCHA here, against `document`. That is
 * a bug in a package Metro also bundles: React Native has no `document`, so
 * phone sign-in crashed on mobile rather than failing with anything useful.
 *
 * The host app supplies it now — see `phoneVerifier` in ../../config.js.
 */
function ensureRecaptcha() {
  if (recaptcha) return recaptcha

  const build = getConfig().phoneVerifier
  if (typeof build !== 'function') {
    throw new ZivError(
      ErrorCode.NOT_IMPLEMENTED,
      getConfig().platform === 'web'
        ? 'Phone sign-in is not configured. Pass phoneVerifier to configureZivapp().'
        : 'Phone sign-in on this platform needs @react-native-firebase/auth and a ' +
          'development build — the Firebase JS SDK cannot run reCAPTCHA here. ' +
          'See docs/FIREBASE-SETUP.md.'
    )
  }

  recaptcha = build({ RecaptchaVerifier, firebaseAuth })
  return recaptcha
}

export const auth = {
  requestCode(phone) {
    return guard(async () => {
      pendingConfirmation = await signInWithPhoneNumber(firebaseAuth, phone, ensureRecaptcha())
      return { verificationId: pendingConfirmation.verificationId }
    })
  },

  confirmCode(verificationId, code) {
    return guard(async () => {
      if (!pendingConfirmation) {
        throw new ZivError(ErrorCode.CODE_EXPIRED, 'Request a new code.')
      }
      const credential = await pendingConfirmation.confirm(code)
      pendingConfirmation = null
      return ensureUserRecord(credential.user)
    })
  },

  signOut() {
    return guard(async () => {
      const id = uidOrNull()
      if (id) {
        await updateDoc(userDoc(id), { isOnline: false, lastSeen: serverTimestamp() }).catch(() => {})
      }
      cachedUser = null
      await fbSignOut(firebaseAuth)
    })
  },

  currentUser() {
    return cachedUser
  },

  onAuthChange(callback) {
    // AuthProvider calls this on mount. Without the guard, a missing
    // .env.local would crash the app at startup rather than showing the
    // signed-out state and the console instructions from ./app.js.
    if (!firebaseAuth) {
      queueMicrotask(() => callback(null))
      return () => {}
    }
    return onAuthStateChanged(firebaseAuth, async account => {
      if (!account) {
        cachedUser = null
        callback(null)
        return
      }
      try {
        cachedUser = await ensureUserRecord(account)
        callback(cachedUser)
      } catch (error) {
        console.error('[ZIvapp] failed to load profile:', error)
        callback(null)
      }
    })
  },

  updateProfile(patch) {
    return guard(async () => {
      const id = uid()
      await updateDoc(userDoc(id), patch)
      const snap = await getDoc(userDoc(id))
      cachedUser = toUser(id, snap.data())
      return cachedUser
    })
  },

  deleteAccount() {
    return guard(async () => {
      const id = uid()
      await deleteDoc(userDoc(id))
      await deleteUser(firebaseAuth.currentUser)
      cachedUser = null
    })
  }
}

/** Create the profile document on first sign-in, then return it. */
async function ensureUserRecord(account) {
  const ref = userDoc(account.uid)
  const snap = await getDoc(ref)
  if (!snap.exists()) {
    const record = {
      phone: account.phoneNumber ?? '',
      displayName: '',
      about: 'Hey there! I am using ZIvapp.',
      avatarUrl: null,
      isOnline: true,
      lastSeen: serverTimestamp(),
      createdAt: serverTimestamp()
    }
    await setDoc(ref, record)
    return toUser(account.uid, { ...record, createdAt: new Date(), lastSeen: new Date() })
  }
  return toUser(account.uid, snap.data())
}

// ---------------------------------------------------------------------------
// chats
// ---------------------------------------------------------------------------

export const chats = {
  subscribeToList(callback) {
    const id = uidOrNull()
    if (!id) return () => {}
    const q = query(
      collection(db, COLLECTIONS.chats),
      where('participantIds', 'array-contains', id),
      orderBy('lastMessageAt', 'desc'),
      fsLimit(100)
    )
    return watch(q, snap => callback(snap.docs.map(d => toChat(d.id, d.data(), id))), 'chats')
  },

  subscribeToChat(chatId, callback) {
    const id = uidOrNull()
    return watch(
      chatDoc(chatId),
      snap => callback(snap.exists() ? toChat(snap.id, snap.data(), id) : null),
      'chat'
    )
  },

  get(chatId) {
    return guard(async () => {
      const snap = await getDoc(chatDoc(chatId))
      return snap.exists() ? toChat(snap.id, snap.data(), uidOrNull()) : null
    })
  },

  getOrCreateDirect(otherUserId) {
    return guard(async () => {
      const me = uid()
      // Deterministic id, so two people opening the chat at once cannot
      // create two documents.
      const id = directChatId(me, otherUserId)
      const ref = chatDoc(id)
      const snap = await getDoc(ref)
      if (!snap.exists()) {
        const record = {
          type: 'direct',
          name: null,
          description: null,
          avatarUrl: null,
          participantIds: [me, otherUserId],
          participants: [
            { userId: me, role: 'member', joinedAt: Timestamp.now() },
            { userId: otherUserId, role: 'member', joinedAt: Timestamp.now() }
          ],
          lastMessage: null,
          lastMessageAt: serverTimestamp(),
          userState: {},
          createdBy: me,
          createdAt: serverTimestamp()
        }
        await setDoc(ref, record)
        return toChat(id, { ...record, createdAt: new Date(), lastMessageAt: new Date() }, me)
      }
      return toChat(id, snap.data(), me)
    })
  },

  createGroup({ name, memberIds, avatarUrl = null }) {
    return guard(async () => {
      const me = uid()
      const participantIds = [...new Set([me, ...memberIds])]
      const record = {
        type: 'group',
        name,
        description: null,
        avatarUrl,
        participantIds,
        participants: participantIds.map(userId => ({
          userId,
          role: userId === me ? 'admin' : 'member',
          joinedAt: Timestamp.now()
        })),
        lastMessage: null,
        lastMessageAt: serverTimestamp(),
        userState: {},
        createdBy: me,
        createdAt: serverTimestamp()
      }
      const ref = await addDoc(collection(db, COLLECTIONS.chats), record)
      return toChat(ref.id, { ...record, createdAt: new Date(), lastMessageAt: new Date() }, me)
    })
  },

  updateGroup(chatId, patch) {
    return guard(() => updateDoc(chatDoc(chatId), patch))
  },

  addMembers(chatId, userIds) {
    return guard(async () => {
      const participants = userIds.map(userId => ({
        userId, role: 'member', joinedAt: Timestamp.now()
      }))
      await updateDoc(chatDoc(chatId), {
        participantIds: arrayUnion(...userIds),
        participants: arrayUnion(...participants)
      })
    })
  },

  removeMember(chatId, userId) {
    return guard(async () => {
      const snap = await getDoc(chatDoc(chatId))
      if (!snap.exists()) throw new ZivError(ErrorCode.NOT_FOUND, 'Chat not found.')
      // participants holds objects, so arrayRemove cannot match on userId alone
      const participants = (snap.data().participants ?? []).filter(p => p.userId !== userId)
      await updateDoc(chatDoc(chatId), {
        participantIds: arrayRemove(userId),
        participants
      })
    })
  },

  setMemberRole(chatId, userId, role) {
    return guard(async () => {
      const snap = await getDoc(chatDoc(chatId))
      if (!snap.exists()) throw new ZivError(ErrorCode.NOT_FOUND, 'Chat not found.')
      const participants = (snap.data().participants ?? []).map(p =>
        p.userId === userId ? { ...p, role } : p
      )
      await updateDoc(chatDoc(chatId), { participants })
    })
  },

  leaveGroup(chatId) {
    return chats.removeMember(chatId, uid())
  },

  setMuted(chatId, muted)       { return setUserFlag(chatId, 'muted', muted) },
  setPinned(chatId, pinned)     { return setUserFlag(chatId, 'pinned', pinned) },
  setArchived(chatId, archived) { return setUserFlag(chatId, 'archived', archived) },

  clearMessages(chatId) {
    return guard(async () => {
      // Client-side delete is capped; a Cloud Function should do this properly
      // once chats get long. 500 is the Firestore batch limit.
      const snap = await getDocs(query(messagesCol(chatId), fsLimit(500)))
      const batch = writeBatch(db)
      snap.docs.forEach(d => batch.delete(d.ref))
      batch.update(chatDoc(chatId), { lastMessage: null })
      await batch.commit()
    })
  },

  deleteChat(chatId) {
    return guard(() => deleteDoc(chatDoc(chatId)))
  }
}

function setUserFlag(chatId, key, value) {
  return guard(() => updateDoc(chatDoc(chatId), { [`userState.${uid()}.${key}`]: value }))
}

// ---------------------------------------------------------------------------
// messages
// ---------------------------------------------------------------------------

export const messages = {
  subscribe(chatId, callback, { limit = 50 } = {}) {
    const q = query(messagesCol(chatId), orderBy('createdAt', 'desc'), fsLimit(limit))
    return watch(q, snap => {
      // Queried newest-first for the limit; the UI wants oldest-first.
      const list = snap.docs.map(d => toMessage(d.id, d.data(), chatId)).reverse()
      callback(list)
    }, 'messages')
  },

  loadOlder(chatId, beforeMessageId, limit = 30) {
    return guard(async () => {
      const cursor = await getDoc(messageDoc(chatId, beforeMessageId))
      if (!cursor.exists()) return []
      const q = query(
        messagesCol(chatId),
        orderBy('createdAt', 'desc'),
        startAfter(cursor),
        fsLimit(limit)
      )
      const snap = await getDocs(q)
      return snap.docs.map(d => toMessage(d.id, d.data(), chatId)).reverse()
    })
  },

  send(chatId, { type, body = '', mediaUrl = null, mediaMeta = null, replyToId = null }) {
    return guard(async () => {
      const me = uid()
      const record = {
        senderId: me,
        type,
        body,
        mediaUrl,
        mediaMeta,
        replyToId,
        status: 'sent',
        reactions: {},
        createdAt: serverTimestamp(),
        editedAt: null,
        deletedAt: null
      }

      const ref = await addDoc(messagesCol(chatId), record)

      // Denormalised preview so the chat list costs one read per chat.
      await updateDoc(chatDoc(chatId), {
        lastMessage: {
          id: ref.id,
          senderId: me,
          type,
          preview: body.slice(0, 120),
          createdAt: Timestamp.now()
        },
        lastMessageAt: serverTimestamp()
      })

      return toMessage(ref.id, { ...record, createdAt: new Date() }, chatId)
    })
  },

  edit(chatId, messageId, body) {
    return guard(() => updateDoc(messageDoc(chatId, messageId), {
      body,
      editedAt: serverTimestamp()
    }))
  },

  remove(chatId, messageId, scope) {
    return guard(async () => {
      if (scope === 'everyone') {
        await updateDoc(messageDoc(chatId, messageId), {
          deletedAt: serverTimestamp(),
          body: '',
          mediaUrl: null,
          mediaMeta: null
        })
      } else {
        await updateDoc(messageDoc(chatId, messageId), {
          hiddenFor: arrayUnion(uid())
        })
      }
    })
  },

  markRead(chatId, messageIds) {
    return guard(async () => {
      if (!messageIds.length) return
      const me = uid()
      const batch = writeBatch(db)
      // Batched on purpose: one write per visible message is the fastest way
      // to burn through the Firestore quota.
      for (const id of messageIds.slice(0, 450)) {
        batch.set(
          doc(db, COLLECTIONS.chats, chatId, COLLECTIONS.messages, id, COLLECTIONS.receipts, me),
          { userId: me, readAt: serverTimestamp() },
          { merge: true }
        )
      }
      batch.update(chatDoc(chatId), { [`userState.${me}.unreadCount`]: 0 })
      await batch.commit()
    })
  },

  react(chatId, messageId, emoji) {
    return guard(() => updateDoc(messageDoc(chatId, messageId), {
      [`reactions.${emoji}`]: arrayUnion(uid())
    }))
  },

  unreact(chatId, messageId) {
    return guard(async () => {
      const me = uid()
      const snap = await getDoc(messageDoc(chatId, messageId))
      if (!snap.exists()) return
      const reactions = { ...(snap.data().reactions ?? {}) }
      for (const emoji of Object.keys(reactions)) {
        reactions[emoji] = reactions[emoji].filter(id => id !== me)
        if (!reactions[emoji].length) delete reactions[emoji]
      }
      await updateDoc(messageDoc(chatId, messageId), { reactions })
    })
  },

  search(chatId, queryText) {
    return guard(async () => {
      // Firestore has no substring search. This is a prefix match only -
      // real full-text search arrives with the MySQL backend.
      const q = query(
        messagesCol(chatId),
        orderBy('body'),
        where('body', '>=', queryText),
        where('body', '<=', queryText + ''),
        fsLimit(50)
      )
      const snap = await getDocs(q)
      return snap.docs.map(d => toMessage(d.id, d.data(), chatId))
    })
  }
}

// ---------------------------------------------------------------------------
// media
// ---------------------------------------------------------------------------

export const media = {
  upload(file, { chatId, kind, onProgress } = {}) {
    return guard(async () => {
      const resolvedKind = kind ?? kindOf(file)

      // Phone photos are several MB; uploading them raw is slow on mobile
      // data and multiplies storage cost for no visible gain.
      let payload = file
      let dimensions = { width: 0, height: 0 }
      if (resolvedKind === 'image') {
        dimensions = await imageDimensions(file)
        payload = await compressImage(file)
      }

      const name = `${Date.now()}_${file.name ?? resolvedKind}`
      const path = `chats/${chatId}/${resolvedKind}/${name}`
      const ref = storageRef(storage, path)
      const task = uploadBytesResumable(ref, payload, { contentType: file.type })

      await new Promise((resolve, reject) => {
        task.on(
          'state_changed',
          snap => onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          error => reject(new ZivError(ErrorCode.UPLOAD_FAILED, 'Upload failed.', { cause: error })),
          resolve
        )
      })

      return {
        url: await getDownloadURL(ref),
        path,
        meta: {
          mimeType: file.type,
          sizeBytes: payload.size ?? file.size,
          fileName: file.name,
          ...dimensions
        }
      }
    })
  },

  remove(path) {
    return guard(() => deleteObject(storageRef(storage, path)))
  },

  async localPreview(file) {
    return URL.createObjectURL(file)
  }
}

// ---------------------------------------------------------------------------
// status
// ---------------------------------------------------------------------------

export const status = {
  subscribeToFeed(callback) {
    const me = uidOrNull()
    if (!me) return () => {}
    // Firestore has no TTL on the free plan, so expiry is a read-time filter.
    const q = query(
      collection(db, COLLECTIONS.status),
      where('expiresAt', '>', Timestamp.now()),
      orderBy('expiresAt', 'desc'),
      fsLimit(200)
    )
    return watch(q, snap => {
      const list = snap.docs
        .map(d => toStatus(d.id, d.data(), me))
        .filter(s => s.userId !== me)
      callback(list)
    }, 'status feed')
  },

  subscribeToMine(callback) {
    const me = uidOrNull()
    if (!me) return () => {}
    const q = query(
      collection(db, COLLECTIONS.status),
      where('userId', '==', me),
      orderBy('createdAt', 'desc')
    )
    return watch(q, snap => callback(snap.docs.map(d => toStatus(d.id, d.data(), me))), 'my status')
  },

  post({ type, body = '', mediaUrl = null, backgroundColor = null, font = null }) {
    return guard(async () => {
      const me = uid()
      const now = Date.now()
      const record = {
        userId: me,
        type,
        body,
        mediaUrl,
        backgroundColor,
        font,
        viewCount: 0,
        viewedBy: [],
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromMillis(now + STATUS_TTL_MS)
      }
      const ref = await addDoc(collection(db, COLLECTIONS.status), record)
      return toStatus(ref.id, { ...record, createdAt: new Date(now) }, me)
    })
  },

  markViewed(statusId) {
    return guard(async () => {
      const me = uid()
      const batch = writeBatch(db)
      batch.set(
        doc(db, COLLECTIONS.status, statusId, COLLECTIONS.views, me),
        { userId: me, viewedAt: serverTimestamp() },
        { merge: true }
      )
      batch.update(doc(db, COLLECTIONS.status, statusId), {
        viewedBy: arrayUnion(me),
        viewCount: increment(1)
      })
      await batch.commit()
    })
  },

  getViewers(statusId) {
    return guard(async () => {
      const snap = await getDocs(collection(db, COLLECTIONS.status, statusId, COLLECTIONS.views))
      return snap.docs.map(d => ({
        userId: d.id,
        viewedAt: d.data().viewedAt?.toDate() ?? new Date()
      }))
    })
  },

  remove(statusId) {
    return guard(() => deleteDoc(doc(db, COLLECTIONS.status, statusId)))
  }
}

// ---------------------------------------------------------------------------
// presence
// ---------------------------------------------------------------------------

const HEARTBEAT_MS = 30_000
const TYPING_DEBOUNCE_MS = 2_000

let heartbeatTimer = null
const typingTimers = new Map()

export const presence = {
  startHeartbeat() {
    const me = uidOrNull()
    if (!me) return () => {}

    const beat = () => {
      updateDoc(userDoc(me), { isOnline: true, lastSeen: serverTimestamp() }).catch(() => {})
    }
    beat()
    // Throttled: writing presence on every interaction is the single most
    // expensive mistake available in a chat app.
    heartbeatTimer = setInterval(beat, HEARTBEAT_MS)

    const goOffline = () => {
      updateDoc(userDoc(me), { isOnline: false, lastSeen: serverTimestamp() }).catch(() => {})
    }

    // 'pagehide' is the browser's last reliable chance to mark someone
    // offline before the tab goes away. React Native has no window, and its
    // equivalent is AppState — which belongs in the app, not in a package
    // Metro also bundles. Guarded rather than assumed.
    const hasWindow = typeof window !== 'undefined' && typeof window.addEventListener === 'function'
    if (hasWindow) window.addEventListener('pagehide', goOffline)

    return () => {
      clearInterval(heartbeatTimer)
      if (hasWindow) window.removeEventListener('pagehide', goOffline)
      goOffline()
    }
  },

  watch(userIds, callback) {
    if (!userIds.length) return () => {}
    // Firestore allows at most 30 values in an 'in' filter.
    const ids = userIds.slice(0, 30)
    const q = query(collection(db, COLLECTIONS.users), where('__name__', 'in', ids))
    return watch(q, snap => {
      const map = {}
      snap.docs.forEach(d => {
        const user = toUser(d.id, d.data())
        map[d.id] = { isOnline: user.isOnline, lastSeen: user.lastSeen }
      })
      callback(map)
    }, 'presence')
  },

  setTyping(chatId, isTyping) {
    const me = uidOrNull()
    if (!me) return

    clearTimeout(typingTimers.get(chatId))
    const ref = doc(db, COLLECTIONS.chats, chatId)

    if (!isTyping) {
      updateDoc(ref, { [`typing.${me}`]: null }).catch(() => {})
      return
    }

    updateDoc(ref, { [`typing.${me}`]: serverTimestamp() }).catch(() => {})
    // Auto-clear, so a dropped connection does not leave someone
    // permanently "typing...".
    typingTimers.set(chatId, setTimeout(() => {
      updateDoc(ref, { [`typing.${me}`]: null }).catch(() => {})
    }, TYPING_DEBOUNCE_MS * 2))
  },

  watchTyping(chatId, callback) {
    const me = uidOrNull()
    return watch(chatDoc(chatId), snap => {
      const typing = snap.data()?.typing ?? {}
      const cutoff = Date.now() - TYPING_DEBOUNCE_MS * 3
      const active = Object.entries(typing)
        .filter(([userId, at]) => userId !== me && at && at.toMillis() > cutoff)
        .map(([userId]) => userId)
      callback(active)
    }, 'typing')
  }
}

// ---------------------------------------------------------------------------
// calls
// ---------------------------------------------------------------------------

const callDoc = id => doc(db, COLLECTIONS.calls, id)

export const calls = {
  start({ chatId, calleeIds, type }) {
    return guard(async () => {
      const me = uid()
      const record = {
        chatId,
        callerId: me,
        calleeIds,
        participantIds: [me, ...calleeIds],
        type,
        status: 'ringing',
        createdAt: serverTimestamp(),
        answeredAt: null,
        endedAt: null
      }
      const ref = await addDoc(collection(db, COLLECTIONS.calls), record)
      return toCall(ref.id, { ...record, createdAt: new Date() })
    })
  },

  accept(callId) {
    return guard(() => updateDoc(callDoc(callId), {
      status: 'connected',
      answeredAt: serverTimestamp()
    }))
  },

  decline(callId) {
    return guard(() => updateDoc(callDoc(callId), {
      status: 'declined',
      endedAt: serverTimestamp()
    }))
  },

  end(callId) {
    return guard(() => updateDoc(callDoc(callId), {
      status: 'ended',
      endedAt: serverTimestamp()
    }))
  },

  subscribeToIncoming(callback) {
    const me = uidOrNull()
    if (!me) return () => {}
    const q = query(
      collection(db, COLLECTIONS.calls),
      where('calleeIds', 'array-contains', me),
      where('status', '==', 'ringing')
    )
    return watch(q, snap => {
      snap.docChanges()
        .filter(change => change.type === 'added')
        .forEach(change => callback(toCall(change.doc.id, change.doc.data())))
    }, 'incoming calls')
  },

  subscribeToCall(callId, callback) {
    return watch(callDoc(callId), snap => {
      if (snap.exists()) callback(toCall(snap.id, snap.data()))
    }, 'call')
  },

  sendOffer(callId, description) {
    return pushSignal(callId, 'offer', { sdp: description.sdp, type: description.type })
  },

  sendAnswer(callId, description) {
    return pushSignal(callId, 'answer', { sdp: description.sdp, type: description.type })
  },

  sendCandidate(callId, candidate) {
    return pushSignal(callId, 'candidate', { ...candidate })
  },

  subscribeToSignals(callId, callback) {
    const me = uidOrNull()
    const q = query(
      collection(db, COLLECTIONS.calls, callId, COLLECTIONS.signals),
      orderBy('createdAt', 'asc')
    )
    return watch(q, snap => {
      snap.docChanges()
        .filter(change => change.type === 'added')
        .map(change => change.doc.data())
        .filter(signal => signal.fromUserId !== me)
        .forEach(signal => callback({
          type: signal.type,
          payload: signal.payload,
          fromUserId: signal.fromUserId
        }))
    }, 'signals')
  },

  subscribeToHistory(callback) {
    const me = uidOrNull()
    if (!me) return () => {}
    const q = query(
      collection(db, COLLECTIONS.calls),
      where('participantIds', 'array-contains', me),
      orderBy('createdAt', 'desc'),
      fsLimit(100)
    )
    return watch(q, snap => callback(snap.docs.map(d => toCall(d.id, d.data()))), 'call history')
  },

  getIceConfig() {
    // STUN only. See the TURN warning in ../contracts/calls.js.
    return DEFAULT_ICE_CONFIG
  }
}

function pushSignal(callId, type, payload) {
  return guard(() => addDoc(
    collection(db, COLLECTIONS.calls, callId, COLLECTIONS.signals),
    { type, payload, fromUserId: uid(), createdAt: serverTimestamp() }
  ))
}

// ---------------------------------------------------------------------------
// contacts
// ---------------------------------------------------------------------------

export const contacts = {
  subscribe(callback) {
    const me = uidOrNull()
    if (!me) return () => {}
    const q = collection(db, COLLECTIONS.users, me, COLLECTIONS.contacts)
    return watch(q, snap => callback(snap.docs.map(d => ({
      id: d.id,
      userId: d.data().userId,
      savedName: d.data().savedName
    }))), 'contacts')
  },

  findByPhone(phone) {
    return guard(async () => {
      const q = query(collection(db, COLLECTIONS.users), where('phone', '==', phone), fsLimit(1))
      const snap = await getDocs(q)
      return snap.empty ? null : toUser(snap.docs[0].id, snap.docs[0].data())
    })
  },

  discover(phones) {
    return guard(async () => {
      const me = uidOrNull()
      const found = []
      // 'in' takes at most 30 values, so page through in chunks.
      for (let i = 0; i < phones.length; i += 30) {
        const chunk = phones.slice(i, i + 30)
        const q = query(collection(db, COLLECTIONS.users), where('phone', 'in', chunk))
        const snap = await getDocs(q)
        snap.docs.forEach(d => {
          if (d.id !== me) found.push(toUser(d.id, d.data()))
        })
      }
      return found
    })
  },

  add({ userId, savedName }) {
    return guard(async () => {
      const me = uid()
      const ref = doc(db, COLLECTIONS.users, me, COLLECTIONS.contacts, userId)
      await setDoc(ref, { userId, savedName, createdAt: serverTimestamp() })
      return { id: userId, userId, savedName }
    })
  },

  remove(contactId) {
    return guard(() => deleteDoc(doc(db, COLLECTIONS.users, uid(), COLLECTIONS.contacts, contactId)))
  },

  block(userId) {
    return guard(() => setDoc(
      doc(db, COLLECTIONS.users, uid(), COLLECTIONS.blocked, userId),
      { userId, createdAt: serverTimestamp() }
    ))
  },

  unblock(userId) {
    return guard(() => deleteDoc(doc(db, COLLECTIONS.users, uid(), COLLECTIONS.blocked, userId)))
  },

  subscribeToBlocked(callback) {
    const me = uidOrNull()
    if (!me) return () => {}
    return watch(
      collection(db, COLLECTIONS.users, me, COLLECTIONS.blocked),
      snap => callback(snap.docs.map(d => d.id)),
      'blocked'
    )
  }
}

// ---------------------------------------------------------------------------
// channels
// ---------------------------------------------------------------------------

/**
 * Not implemented on Firebase, deliberately and visibly.
 *
 * ZIvapp runs on the `rest` backend — our own Node and MySQL server — which
 * is where channels are live. Firestore is kept in step for everything that
 * predates it, but channels are phase 8 in docs/CHANNELS.md: wanted only if
 * broadcast has to work on Firebase before the migration.
 *
 * The reason it is not a quick port: the follower list must be unreadable by
 * every other user, admins included. On MySQL that is a query nobody wrote.
 * On Firestore it is a rules file somebody can later edit, so it needs its
 * own rules work and its own tests before it can be trusted with the
 * promise. A half-done version that leaks followers is worse than none.
 *
 * Generated from the contract so this cannot drift out of step as methods
 * are added, and so the error names the method rather than failing vaguely.
 */
export const channels = Object.fromEntries(
  REQUIRED_METHODS.channels.map(method => [
    method,
    method.startsWith('subscribe')
      ? () => {
          console.warn(`[ZIvapp] channels.${method}() is not implemented on Firebase.`)
          return () => {}
        }
      : () => {
          throw new ZivError(
            ErrorCode.NOT_IMPLEMENTED,
            `channels.${method}() is not implemented on the Firebase provider. ` +
            'Channels run on the rest backend; see docs/CHANNELS.md phase 8.'
          )
        }
  ])
)

// ---------------------------------------------------------------------------
// communities
// ---------------------------------------------------------------------------

/**
 * Not implemented on Firebase, deliberately and visibly — the same position
 * as channels above.
 *
 * ZIvapp runs on the `rest` backend. Porting communities to Firestore is
 * not hard in principle, but a community is defined by a relationship
 * between documents in two collections (a chat naming its parent, a
 * community naming its announcement chat), and Firestore has no foreign
 * keys and no transactions across collections in security rules. Getting
 * "only admins may post in the announcement group" right there is rules
 * work with its own tests, not a translation.
 */
export const communities = Object.fromEntries(
  REQUIRED_METHODS.communities.map(method => [
    method,
    method.startsWith('subscribe')
      ? () => {
          console.warn(`[ZIvapp] communities.${method}() is not implemented on Firebase.`)
          return () => {}
        }
      : () => {
          throw new ZivError(
            ErrorCode.NOT_IMPLEMENTED,
            `communities.${method}() is not implemented on the Firebase provider. ` +
            'Communities run on the rest backend.'
          )
        }
  ])
)
